import {
  Download,
  ExternalLink,
  FileText,
  Image as ImageIcon,
  Link as LinkIcon,
  Loader2,
  Eye,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const IMAGE_MIME_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/heic",
  "image/heif",
  "image/webp",
]);

type SharedFileAttachment = {
  id: string;
  sortOrder: number;
  displayName: string;
  fileUpload: {
    id: string;
    mimeType: string;
    fileSizeBytes: number;
    scanStatus: string;
  };
};

type SharedLink = {
  id: string;
  url: string;
  label: string | null;
  sortOrder: number;
};

interface SharedResourceListProps {
  files: SharedFileAttachment[];
  links: SharedLink[];
  className?: string;
  inFlightId: string | null;
  inFlightAction: "download" | "preview" | null;
  onPreviewFile: (attachment: SharedFileAttachment) => void;
  onDownloadFile: (attachment: SharedFileAttachment) => void;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) {
    return "0 B";
  }
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function getHost(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

function isPreviewable(mimeType: string): boolean {
  return mimeType === "application/pdf" || mimeType.startsWith("image/");
}

export function SharedResourceList({
  files,
  links,
  className,
  inFlightId,
  inFlightAction,
  onPreviewFile,
  onDownloadFile,
}: SharedResourceListProps) {
  const sortedLinks = links.slice().sort((a, b) => a.sortOrder - b.sortOrder);
  const sortedFiles = files.slice().sort((a, b) => a.sortOrder - b.sortOrder);

  if (sortedLinks.length === 0 && sortedFiles.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      <div className="space-y-2">
        {sortedFiles.map((attachment) => {
          const { fileUpload } = attachment;
          const isImage = IMAGE_MIME_TYPES.has(fileUpload.mimeType);
          const FileIcon = isImage ? ImageIcon : FileText;
          const isPending =
            fileUpload.scanStatus === "pending" ||
            fileUpload.scanStatus === "scanning";
          const isClean = fileUpload.scanStatus === "clean";
          if (!isClean && !isPending) {
            return null;
          }

          const canPreview = isClean && isPreviewable(fileUpload.mimeType);
          const isThisInFlight = inFlightId === fileUpload.id;

          return (
            <div
              key={attachment.id}
              className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white px-3 py-3 shadow-sm sm:flex-row sm:items-center"
            >
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                  <FileIcon size={18} />
                </span>
                <div className="min-w-0 flex-1">
                  <p
                    className="truncate text-sm font-medium text-gray-900"
                    title={attachment.displayName}
                  >
                    {attachment.displayName}
                  </p>
                  <p className="text-xs text-gray-500">
                    {isPending
                      ? "Scanning before it can be opened"
                      : formatBytes(fileUpload.fileSizeBytes)}
                  </p>
                </div>
              </div>

              <div className="flex shrink-0 gap-2 sm:justify-end">
                {canPreview && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!!inFlightId}
                    className="h-8 flex-1 border-blue-200 text-blue-700 hover:bg-blue-50 sm:flex-none"
                    onClick={() => onPreviewFile(attachment)}
                  >
                    {isThisInFlight && inFlightAction === "preview" ? (
                      <Loader2 size={14} className="mr-1.5 animate-spin" />
                    ) : (
                      <Eye size={14} className="mr-1.5" />
                    )}
                    View
                  </Button>
                )}
                {!isPending && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!!inFlightId}
                    className="h-8 flex-1 sm:flex-none"
                    onClick={() => onDownloadFile(attachment)}
                  >
                    {isThisInFlight && inFlightAction === "download" ? (
                      <Loader2 size={14} className="mr-1.5 animate-spin" />
                    ) : (
                      <Download size={14} className="mr-1.5" />
                    )}
                    Download
                  </Button>
                )}
              </div>
            </div>
          );
        })}

        {sortedLinks.map((link) => (
          <a
            key={link.id}
            href={link.url}
            target="_blank"
            rel="noreferrer"
            className="flex flex-col gap-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-3 text-blue-800 transition-colors hover:border-blue-300 hover:bg-blue-100 sm:flex-row sm:items-center"
          >
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-blue-600">
                <LinkIcon size={18} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">
                  {link.label?.trim() || "Open link"}
                </p>
                <p className="truncate text-xs text-blue-600">
                  {getHost(link.url)}
                </p>
              </div>
            </div>
            <span className="inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-md bg-white px-3 text-sm font-medium text-blue-700 shadow-sm">
              Open
              <ExternalLink size={14} />
            </span>
          </a>
        ))}
      </div>
    </div>
  );
}
